'use client'

/**
 * The AI Employee's on-screen identity (plan 03 CHG-05 UI half, decision D4).
 *
 * One name everywhere, read from one Setting (`assistant_persona_name`, plan 02)
 * plus display details (`ai_employee_persona`, plan 03), so the header pill, the
 * chat panel, the apply pages and the voice prompts cannot drift apart. The
 * noun on screen is always "AI Employee", never "AI assistant".
 *
 * Two sources feed the same hook:
 * - Signed-in shell: SignedInAssistantPersonaProvider reads GET /api/ai/persona.
 * - Public apply pages (no session): AssistantPersonaProvider receives the
 *   `assistant` block of GET /api/public/requisitions/{id}.
 */

import { createContext, useContext, useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { apiClient } from '@/lib/api-client'

export type PersonaLanguage = 'en' | 'es'

export interface AssistantPersona {
  name: string
  roleLabel: string
  publicLabels: Record<PersonaLanguage, string>
  photo: string
}

/** Shapes returned by the backend (app/services/ai_persona.py). */
export interface AssistantPersonaResponse {
  name: string
  role_label: string
  public_label: string
  public_labels: Record<string, string>
  photo: string
}

export interface PublicAssistantResponse {
  name: string
  public_labels: Record<string, string>
  photo: string
}

/**
 * Shown until the Setting arrives (first paint, or a session that may not read
 * it). Mirrors the backend defaults and the seed, so the pill never flashes an
 * empty name. Display copy only, not a business rule.
 */
export const FALLBACK_PERSONA: AssistantPersona = {
  name: 'Ava',
  roleLabel: 'Driver Recruiting AI Employee',
  publicLabels: {
    en: "Meridian Freight Lines' AI Employee",
    es: 'la empleada de IA de Meridian Freight Lines',
  },
  photo: 'ava',
}

/** Roles that GET /api/ai/persona allows (app/authz.map.json). */
const PERSONA_READER_ROLES = ['admin', 'user', 'logistics_leader', 'panelist']

function labelsFrom(raw: Record<string, string> | undefined): Record<PersonaLanguage, string> {
  return {
    en: raw?.en || FALLBACK_PERSONA.publicLabels.en,
    es: raw?.es || FALLBACK_PERSONA.publicLabels.es,
  }
}

export function personaFromApi(body: AssistantPersonaResponse): AssistantPersona {
  return {
    name: body.name || FALLBACK_PERSONA.name,
    roleLabel: body.role_label || FALLBACK_PERSONA.roleLabel,
    publicLabels: labelsFrom(body.public_labels),
    photo: body.photo || FALLBACK_PERSONA.photo,
  }
}

export function personaFromPublic(body: PublicAssistantResponse | null | undefined): AssistantPersona {
  if (!body) return FALLBACK_PERSONA
  return {
    ...FALLBACK_PERSONA,
    name: body.name || FALLBACK_PERSONA.name,
    publicLabels: labelsFrom(body.public_labels),
    photo: body.photo || FALLBACK_PERSONA.photo,
  }
}

/** "la empleada de IA ..." starts lower case for use mid-sentence; capitalise it on its own line. */
export function capitalizeFirst(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
}

const AssistantPersonaContext = createContext<AssistantPersona>(FALLBACK_PERSONA)

export function useAssistantPersona(): AssistantPersona {
  return useContext(AssistantPersonaContext)
}

/** Static provider for pages that already hold the persona (public apply flow). */
export function AssistantPersonaProvider({
  persona,
  children,
}: {
  persona: AssistantPersona
  children: React.ReactNode
}) {
  return <AssistantPersonaContext.Provider value={persona}>{children}</AssistantPersonaContext.Provider>
}

/**
 * Signed-in provider. Fetches once per signed-in user, and only when the
 * session holds a role the endpoint allows: an unauthenticated call would send
 * the sign-in page into a Keycloak redirect, and a pending user would only
 * collect a 403. Any failure keeps the fallback silently.
 */
export function SignedInAssistantPersonaProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  const [persona, setPersona] = useState<AssistantPersona>(FALLBACK_PERSONA)
  const canRead =
    status === 'authenticated' && (session?.roles ?? []).some((role) => PERSONA_READER_ROLES.includes(role))
  const userKey = session?.sub ?? session?.user?.email ?? ''

  useEffect(() => {
    if (!canRead) return
    let cancelled = false
    apiClient
      .get<AssistantPersonaResponse>('/api/ai/persona')
      .then((body) => {
        if (!cancelled) setPersona(personaFromApi(body))
      })
      .catch(() => {
        // Keep the fallback; the persona is display copy and must never block the shell.
      })
    return () => {
      cancelled = true
    }
  }, [canRead, userKey])

  return <AssistantPersonaContext.Provider value={persona}>{children}</AssistantPersonaContext.Provider>
}
